import { Button, ButtonGroup, LuIcon } from "@overmap-ai/blocks"
import { useObjectUrl } from "@overmap-ai/forms"
import { LngLatBounds } from "mapbox-gl"
import { memo, useCallback, useEffect, useId, useMemo, useState } from "react"
import { useMap } from "react-map-gl/mapbox"

import { EditableMapSection, Layer, Source } from "../../mapbox/components"
import { BOUNDS_PADDING, WORLD_LNG_LAT_BOUNDS } from "../../mapbox/constants"
import { MapSlotLayerId } from "../../mapbox/enums"
import { modelIconMapping } from "../../mappings/icons"
import { BottomBar } from "../BottomBar/BottomBar"

interface ImageOverlayPickerOverlayProps {
	initialBounds?: LngLatBounds
	file: File
	onConfirm: (bounds: LngLatBounds) => void
	onCancel: () => void
}

export const ImageOverlayPickerOverlay = memo((props: ImageOverlayPickerOverlayProps) => {
	const { initialBounds, file, onConfirm, onCancel } = props

	const layerId = useId()
	const { current: map } = useMap()
	const [bounds, setBounds] = useState<LngLatBounds>(WORLD_LNG_LAT_BOUNDS)

	useEffect(() => {
		if (!map) return

		if (initialBounds) {
			map?.fitBounds(initialBounds, { padding: BOUNDS_PADDING, animate: false })
			setBounds(initialBounds)
		} else {
			setBounds(map.getCenter().toBounds(1000))
		}
	}, [initialBounds, map])

	const handleConfirm = useCallback(() => {
		onConfirm(bounds)
	}, [bounds, onConfirm])

	const objectUrl = useObjectUrl(file)

	const coordinates = useMemo(() => {
		const sw = bounds.getSouthWest()
		const ne = bounds.getNorthEast()

		return [
			[sw.lng, ne.lat],
			[ne.lng, ne.lat],
			[ne.lng, sw.lat],
			[sw.lng, sw.lat],
		] satisfies [[number, number], [number, number], [number, number], [number, number]]
	}, [bounds])

	return (
		<>
			<EditableMapSection bounds={bounds} onBoundsChange={setBounds} enableEdit>
				{objectUrl ? (
					<Source type="image" coordinates={coordinates} url={objectUrl}>
						<Layer id={layerId} type="raster" beforeId={MapSlotLayerId.PROJECT_FILES} />
					</Source>
				) : null}
			</EditableMapSection>
			<BottomBar className="flex flex-col gap-2">
				<ButtonGroup className="flex w-full gap-2" size="sm" variant="soft" accentColor="base">
					<Button onClick={onCancel} className="grow">
						<LuIcon icon={modelIconMapping.close} />
						Cancel
					</Button>
					<Button onClick={handleConfirm} className="grow" accentColor="primary" variant="surface">
						<LuIcon icon={modelIconMapping.check} />
						Confirm
					</Button>
				</ButtonGroup>
			</BottomBar>
		</>
	)
})
ImageOverlayPickerOverlay.displayName = "ImageOverlayPickerOverlay"
